import React, { useState } from "react";
import { Plus, Trash2, Download, Loader2, Check } from "lucide-react";
import api, { formatError, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

export const VAT_TYPES = [["vat12", "12% VAT"], ["non_vat", "Non-VAT"], ["zero", "Zero-rated"]];

const cls = "bg-slate-950 border-slate-700";

export const downloadInvoicePdf = async (shipmentId, name) => {
  try {
    const token = localStorage.getItem("lac_token");
    const res = await fetch(`${API}/shipments/${shipmentId}/invoice.pdf`, { credentials: "include", headers: token ? { Authorization: `Bearer ${token}` } : {} });
    if (!res.ok) throw new Error("Download failed");
    const url = window.URL.createObjectURL(await res.blob());
    const a = document.createElement("a");
    a.href = url; a.download = `${name}.pdf`;
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
  } catch (e) { toast.error(e.message); }
};

export default function InvoiceForm({ sh, onSaved }) {
  const inv = sh.billing_info || {};
  const tr = sh.transmittal || {};
  const [clientName, setClientName] = useState(inv.client_name || sh.documentation?.consignee || sh.documentation?.client_name || sh.pre_alert?.sender_name || "");
  const [clientAddr, setClientAddr] = useState(inv.client_address || "");
  const [lines, setLines] = useState(inv.lines?.length ? inv.lines.map((l) => ({ vat_type: "vat12", ...l })) : (tr.charges?.length ? tr.charges.filter((c) => c.amount).map((c) => ({ ...c, vat_type: "vat12" })) : [{ description: "", amount: 0, vat_type: "vat12" }]));
  const [discType, setDiscType] = useState(inv.discount_type || "amount");
  const [discValue, setDiscValue] = useState(inv.discount_value ?? 0);
  const [dueDate, setDueDate] = useState(inv.due_date || "");
  const [notes, setNotes] = useState(inv.notes || "");
  const [loading, setLoading] = useState(false);

  const by = (t) => lines.filter((l) => (l.vat_type || "vat12") === t).reduce((s, l) => s + (parseFloat(l.amount) || 0), 0);
  const vatable = by("vat12"), nonVat = by("non_vat"), zero = by("zero");
  const tax = vatable * 0.12;
  const gross = vatable + nonVat + zero + tax;
  const discount = discType === "percent" ? gross * (parseFloat(discValue) || 0) / 100 : (parseFloat(discValue) || 0);
  const total = gross - discount;
  const upd = (i, k, v) => setLines(lines.map((l, idx) => (idx === i ? { ...l, [k]: v } : l)));

  const save = async () => {
    setLoading(true);
    try {
      await api.post(`/shipments/${sh.id}/billing`, {
        client_name: clientName, client_address: clientAddr, due_date: dueDate, notes,
        lines: lines.map((l) => ({ description: l.description, amount: parseFloat(l.amount) || 0, vat_type: l.vat_type || "vat12" })),
        discount_type: discType, discount_value: parseFloat(discValue) || 0,
      });
      toast.success(inv.invoice_number ? "Invoice updated" : "Invoice created"); onSaved();
    } catch (e) { toast.error(formatError(e)); } finally { setLoading(false); }
  };

  return (
    <div className="space-y-4">
      <div className="text-xs text-slate-500">
        Shipment <span className="font-mono-ops text-slate-300">{sh.shipment_number}</span>
        {tr.transmittal_number && <> · Transmittal <span className="font-mono-ops text-slate-300">{tr.transmittal_number}</span></>}
        {tr.mawb_number && <> · MAWB <span className="font-mono-ops text-slate-300">{tr.mawb_number}</span></>}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div><Label>Bill to</Label><Input data-testid="input-client-name" value={clientName} onChange={(e) => setClientName(e.target.value)} className={cls} /></div>
        <div><Label>Client address</Label><Input data-testid="input-client-address" value={clientAddr} onChange={(e) => setClientAddr(e.target.value)} className={cls} /></div>
        <div><Label>Due date (default 30 days)</Label><Input data-testid="input-due-date" type="date" value={dueDate?.slice(0, 10) || ""} onChange={(e) => setDueDate(e.target.value)} className={cls} /></div>
      </div>
      <div className="space-y-2">
        {lines.map((l, i) => (
          <div key={i} className="flex flex-wrap sm:flex-nowrap gap-2">
            <Input data-testid={`inv-desc-${i}`} value={l.description} onChange={(e) => upd(i, "description", e.target.value)} placeholder="Description" className={`w-full sm:flex-1 ${cls}`} />
            <Select value={l.vat_type || "vat12"} onValueChange={(v) => upd(i, "vat_type", v)}>
              <SelectTrigger data-testid={`inv-vat-${i}`} className={`w-32 ${cls}`}><SelectValue /></SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
                {VAT_TYPES.map(([v, lb]) => <SelectItem key={v} value={v}>{lb}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input data-testid={`inv-amount-${i}`} type="number" value={l.amount} onChange={(e) => upd(i, "amount", e.target.value)} className={`w-32 text-right font-mono-ops ${cls}`} />
            <Button size="sm" variant="ghost" onClick={() => setLines(lines.filter((_, idx) => idx !== i))} className="text-rose-300 hover:bg-rose-500/10"><Trash2 size={14} /></Button>
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between">
        <Button data-testid="button-add-line" variant="outline" size="sm" onClick={() => setLines([...lines, { description: "", amount: 0, vat_type: "vat12" }])} className="border-slate-700 hover:bg-slate-800"><Plus size={14} className="mr-1" /> Add line</Button>
        <div className="flex items-center gap-2"><Label className="text-xs">Discount (after VAT)</Label>
          <Select value={discType} onValueChange={setDiscType}>
            <SelectTrigger data-testid="select-discount-type" className={`w-20 ${cls}`}><SelectValue /></SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-700 text-slate-100"><SelectItem value="amount">₱</SelectItem><SelectItem value="percent">%</SelectItem></SelectContent>
          </Select>
          <Input data-testid="input-discount-value" type="number" min="0" value={discValue} onChange={(e) => setDiscValue(e.target.value)} className={`w-24 text-right font-mono-ops ${cls}`} /></div>
      </div>
      <div><Label>Notes</Label><Textarea data-testid="input-invoice-notes" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className={cls} /></div>
      <div className="bg-slate-950/70 border border-slate-800 rounded-md p-4 space-y-1 font-mono-ops text-sm">
        {[["VATable sales", vatable, "inv-sum-vatable"], ["Non-VAT sales", nonVat, "inv-sum-nonvat"], ["Zero-rated sales", zero, "inv-sum-zero"], ["VAT (12%)", tax, "inv-sum-vat"], ["Less: discount", -discount, "inv-sum-discount"]].map(([lb, v, t]) => (
          <div key={t} className="flex justify-between"><span className="text-slate-500">{lb}</span><span data-testid={t}>{v.toFixed(2)}</span></div>
        ))}
        <div className="flex justify-between text-lg font-bold text-sky-300 border-t border-slate-800 pt-2 mt-2"><span>TOTAL</span><span data-testid="invoice-total">{total.toFixed(2)}</span></div>
      </div>
      <div className="flex gap-2 flex-wrap">
        <Button data-testid="button-generate-invoice" onClick={save} disabled={loading} className="bg-blue-600 hover:bg-blue-500">
          {loading ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Check size={14} className="mr-2" />} {inv.invoice_number ? "Update invoice" : "Create invoice"}
        </Button>
        {inv.invoice_number && (
          <Button data-testid="button-download-invoice-pdf" onClick={() => downloadInvoicePdf(sh.id, inv.invoice_number)} className="bg-emerald-600 hover:bg-emerald-500">
            <Download size={14} className="mr-2" /> Download PDF
          </Button>
        )}
      </div>
    </div>
  );
}
